import type { TFunction } from 'i18next';
import { isEmpty, isEqual, isString, toArray, toString } from 'lodash-es';
import type { FieldError, FieldErrors, FieldPath } from 'react-hook-form';
import type { SchemaOf } from 'yup';
import { array, boolean, mixed, number, object, string } from 'yup';
import type { CreateApplicationVersionArtifacts } from '../../../domain/models/applicationVersion.model';
import type { ArtifactComponentTypeEnum } from '../../../domain/models/artifact.model';
import type { DeviceManagementArtifact } from '../../../domain/models/deviceManagementArtifacts.model';
import type { OsResourceApiMapped } from '../../../domain/models/osList.model';
import type { SystemPackageVersionTemplateAssociations } from '../../../domain/models/systemPackage.model';
import { SystemPackageArtifactType } from '../../../domain/models/systemPackage.model';
import type {
  AppVersion,
  AppVersionExistingAppFiles,
} from '../../../pages/applications-version-details-page/applicationVersionDetails.model';
import type {
  AppVersionApplicationFile,
  ParameterTemplate,
  SystemPackageAppVersion,
} from '../../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import { AppVersionFileUploadErrorStatus } from '../../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import { versionRegex } from '../../../utils/versionRegex.util';
import { noFileErrorsTest } from '../../../utils/fileUploadValidation.util';
import { allowedAndRequiredTargetLocationExtensionsForBuild } from '../../application-version-fields/applicationVersionFields.util';
import type { SystemPackageVersionAutocompleteFieldValue } from '../../system-package-version-fields/systemPackageVersionFields.model';
import { SystemPackageVersionFormError } from '../systemPackageVersionSteps.model';

export function getFieldRequiredMessage(_?: FieldPath<SystemPackageAppVersion>): string {
  return 'This field is required';
}

export function getInvalidFieldFormat(t: TFunction, fieldPath?: FieldPath<AppVersion>): string {
  return fieldPath === 'definition.version' ? t('error.invalidVersion') : t('genericError.invalidFormat');
}

export function createSystemPackageVersionSchema(
  getFieldRequiredMessageCallback: (fieldPath?: FieldPath<SystemPackageAppVersion>) => string,
  componentType: Extract<ArtifactComponentTypeEnum, 'APPLICATION' | 'SYSTEM_PACKAGE'>,
  t: TFunction,
): SchemaOf<SystemPackageAppVersion> {
  // This incorrectly report optional string in the typings
  const appVersionAutocompleteFieldValueSchema = array(
    object({
      label: string().required(getFieldRequiredMessageCallback()),
      value: string().required(getFieldRequiredMessageCallback()),
      order: number(),
    }),
  );

  return object().shape({
    build: object().shape(
      {
        existingAppFiles: appVersionAutocompleteFieldValueSchema
          .default([])
          .when('applicationFiles', {
            is: (applicationFiles: AppVersionExistingAppFiles[]) => Boolean(applicationFiles?.length),
            then: array(),
            otherwise: array().required(getFieldRequiredMessageCallback()).min(1, getFieldRequiredMessageCallback()),
          })
          .test(
            SystemPackageVersionFormError.NoEmptyNamesInDeploymentRules,
            getFieldRequiredMessageCallback(),
            function validate(applicationFiles) {
              const emptyValues = (applicationFiles as unknown as AppVersionExistingAppFiles[])?.reduce(
                (accum, current) => {
                  const emptyDownloadPolicies = current.downloadPolicy?.reduce(
                    (downloadPolicyAccum, downloadPolicy, index) => ({
                      ...downloadPolicyAccum,
                      ...(isEmpty(downloadPolicy.name) ? { [index]: getFieldRequiredMessageCallback() } : {}),
                    }),
                    {} as Record<string, string>,
                  );
                  return {
                    ...accum,
                    ...(isEmpty(current.downloadPolicy) || Object.keys(emptyDownloadPolicies).length === 0
                      ? {}
                      : { [toString(current.value)]: emptyDownloadPolicies }),
                  };
                },
                {} as Record<string, Record<string, string>>,
              );
              if (Object.keys(emptyValues).length) {
                return this.createError({ message: JSON.stringify(emptyValues) });
              }
              return true;
            },
          ),
        applicationFiles: array()
          .default([])
          .of(
            object().shape({
              id: string().required(getFieldRequiredMessageCallback()),
              template: array()
                .default([])
                .of(
                  object().shape({
                    name: string().required(getFieldRequiredMessageCallback()),
                    file: mixed().required(getFieldRequiredMessageCallback()),
                    size: number().required(getFieldRequiredMessageCallback()),
                    checksum: string().required(getFieldRequiredMessageCallback()),
                    extension: string(),
                    error: boolean(),
                    id: string(),
                    uploadId: string(),
                    uploadStatus: mixed<AppVersionFileUploadErrorStatus>()
                      .oneOf(Object.values(AppVersionFileUploadErrorStatus))
                      .default(AppVersionFileUploadErrorStatus.None),
                    type: mixed<SystemPackageArtifactType>()
                      .oneOf(Object.values(SystemPackageArtifactType))
                      .default(SystemPackageArtifactType.SwPackage),
                    componentType:
                      mixed<
                        SystemPackageAppVersion['build']['applicationFiles'][number]['template'][number]['componentType']
                      >().default(componentType),
                    order: number(),
                    downloadPolicy: array().of(
                      object().shape({
                        name: string().required(getFieldRequiredMessageCallback()),
                        version: string(),
                      }),
                    ),
                  }),
                )
                .test('no-errors-in-template-files', '', function validate(template) {
                  if (template?.some((file) => file.error === true)) {
                    return this.createError({ message: '' });
                  }
                  return true;
                }),
              targetLocation: string().when('template', {
                is: (template: AppVersionApplicationFile[]) =>
                  isFileExtensionRequiredForTargetLocation(getFileExtensionFromFilename(toString(template?.[0]?.name))),
                then: string().required(getFieldRequiredMessageCallback()),
                otherwise: string(),
              }),
            }),
          )
          .when('existingAppFiles', {
            is: (files: SystemPackageVersionAutocompleteFieldValue[]) => Boolean(files?.length),
            then: array(),
            otherwise: array()
              .default([])
              .required(getFieldRequiredMessageCallback())
              .min(1, getFieldRequiredMessageCallback()),
          })
          .test(noFileErrorsTest(t))
          .test(
            SystemPackageVersionFormError.NoEmptyNamesInDeploymentRules,
            getFieldRequiredMessageCallback(),
            function validate(applicationFiles) {
              const emptyValues = applicationFiles?.reduce(
                (accum, current) => {
                  const files = current.template?.reduce(
                    (templateAccum, applicationFile) => {
                      const emptyDownloadPolicies = applicationFile.downloadPolicy?.reduce(
                        (downloadPolicyAccum, downloadPolicy, index) => ({
                          ...downloadPolicyAccum,
                          ...(isEmpty(downloadPolicy.name) ? { [index]: getFieldRequiredMessageCallback() } : {}),
                        }),
                        {} as Record<string, string>,
                      );

                      return {
                        ...templateAccum,
                        ...(isEmpty(applicationFile.downloadPolicy) || Object.keys(emptyDownloadPolicies).length === 0
                          ? {}
                          : { [applicationFile.id]: emptyDownloadPolicies }),
                      };
                    },
                    {} as Record<string, Record<string, string>>,
                  );
                  return {
                    ...accum,
                    ...files,
                  };
                },
                {} as Record<string, Record<string, string>>,
              );
              if (Object.keys(emptyValues).length) {
                return this.createError({ message: JSON.stringify(emptyValues) });
              }
              return true;
            },
          ),
      },
      [['existingAppFiles', 'applicationFiles']],
    ),
    configure: object().shape({
      existingTemplates: appVersionAutocompleteFieldValueSchema.default([]),
      parameterTemplates: array()
        .default([])
        .of(
          object().shape({
            id: string().required(getFieldRequiredMessageCallback()),
            targetLocation: string().required(getFieldRequiredMessageCallback()),
            template: array()
              .default([])
              .of(
                object().shape({
                  name: string().required(getFieldRequiredMessageCallback()),
                  file: mixed().required(getFieldRequiredMessageCallback()),
                  size: number().required(getFieldRequiredMessageCallback()),
                  checksum: string().required(getFieldRequiredMessageCallback()),
                  extension: string(),
                  error: boolean(),
                  id: string(),
                  uploadId: string(),
                  uploadStatus: mixed<AppVersionFileUploadErrorStatus>()
                    .oneOf(Object.values(AppVersionFileUploadErrorStatus))
                    .default(AppVersionFileUploadErrorStatus.None),
                  type: mixed<SystemPackageArtifactType>()
                    .oneOf(Object.values(SystemPackageArtifactType))
                    .default(SystemPackageArtifactType.Template),
                  componentType:
                    mixed<
                      SystemPackageAppVersion['configure']['parameterTemplates'][number]['template'][number]['componentType']
                    >().default(componentType),
                }),
              )
              .test('no-errors-in-template-files', '', function validate(template) {
                if (template?.some((file) => file.error === true)) {
                  return this.createError({ message: '' });
                }
                return true;
              }),
          }),
        )
        .test(noFileErrorsTest(t)),
    }),
    definition: object().shape({
      version: string()
        .required(getFieldRequiredMessageCallback())
        .matches(versionRegex, getInvalidFieldFormat(t, 'definition.version')),
      releaseNotes: string().required(getFieldRequiredMessageCallback()),
      operationalSystemDependency: string().required(getFieldRequiredMessageCallback()),
      minimumOSVersion: appVersionAutocompleteFieldValueSchema
        .required(getFieldRequiredMessageCallback())
        .min(1, getFieldRequiredMessageCallback()),
      systemPackageDependency: string(),
      minimumSystemPackageVersion: array().when(['systemPackageDependency'], {
        is: (value?: string) => !!value,
        then: appVersionAutocompleteFieldValueSchema
          .required(getFieldRequiredMessageCallback())
          .min(1, getFieldRequiredMessageCallback()),
        otherwise: appVersionAutocompleteFieldValueSchema.default([]),
      }),
      appDependency: string(),
      minimumAppVersion: array().when('appDependency', {
        is: (value?: string) => !!value,
        then: appVersionAutocompleteFieldValueSchema
          .required(getFieldRequiredMessageCallback())
          .min(1, getFieldRequiredMessageCallback()),
        otherwise: appVersionAutocompleteFieldValueSchema.default([]),
      }),
    }),
  }) as unknown as SchemaOf<SystemPackageAppVersion>;
}

export function getTemplateFilesOnly(parameterTemplates?: ParameterTemplate[]): AppVersionApplicationFile[] {
  return toArray(
    parameterTemplates?.reduce(
      (accum, { template, targetLocation }) => [...accum, ...template.map((file) => ({ ...file, targetLocation }))],
      [] as AppVersionApplicationFile[],
    ),
  );
}

export function getDefinitionHiddenFields(): FieldPath<SystemPackageAppVersion>[] {
  return [
    'definition.systemPackageDependency',
    'definition.minimumSystemPackageVersion',
    'definition.appDependency',
    'definition.minimumAppVersion',
  ];
}

export function transformArtifactsToOptions(
  componentType: AppVersionApplicationFile['componentType'],
  artifacts?: DeviceManagementArtifact[],
): AppVersionApplicationFile[] | undefined {
  return artifacts?.map(
    ({ checksum, name, size, id, targetLocation, templateFileId, templateFileVersion, templateFileName }) => ({
      name,
      size,
      id,
      checksum,
      componentType,
      targetLocation,
      templateFileId,
      templateFileVersion,
      templateFileName,
    }),
  );
}

export function transformOsVersionsListForField(
  osList?: OsResourceApiMapped[],
): SystemPackageVersionAutocompleteFieldValue[] | undefined {
  return osList?.map(({ id: value = '', name: label = '' }) => ({ value, label }));
}

export function getAssociatedTemplatesFromUploadedFiles(
  uploadedFiles: AppVersionApplicationFile[],
): SystemPackageVersionTemplateAssociations[] {
  return uploadedFiles
    .filter(({ type: artifactType }) => artifactType === SystemPackageArtifactType.Template)
    .map(({ targetLocation = '', uploadId: id = '' }) => ({ artifactId: id, targetLocation }));
}

export function getArticactIdsFromFormValues(
  formValues: SystemPackageAppVersion,
  uploadedFiles: AppVersionApplicationFile[],
): CreateApplicationVersionArtifacts[] {
  return [
    ...toArray(formValues.build.existingAppFiles).map(({ value, order = 0, targetLocation }) => ({
      id: value,
      order,
      targetLocation,
    })),
    ...uploadedFiles
      .filter(({ type: artifactType }) => artifactType === SystemPackageArtifactType.SwPackage)
      .map(({ uploadId: id = '', order = 0, targetLocation }) => ({
        id,
        order,
        ...(isString(targetLocation) && targetLocation?.length >= 1 && { targetLocation }),
      })),
  ];
}

export function getCompatibleVersionsIdsFromFormValues(formValues: SystemPackageAppVersion): string[] {
  return toArray(formValues.definition.minimumOSVersion).map(({ value }) => value);
}

export function getSelectedOsVersion(values: SystemPackageAppVersion): string | undefined {
  return values?.definition?.operationalSystemDependency;
}

export function mapExistingTemplatesTargetLocationsIntoAssociations(
  existingTargetLocations?: Record<string, string>,
): SystemPackageVersionTemplateAssociations[] {
  return existingTargetLocations
    ? Object.keys(existingTargetLocations).map((key) => ({
        artifactId: key,
        targetLocation: existingTargetLocations[key],
      }))
    : [];
}

export function getAllFilesForUpload(
  formApplicationFiles: AppVersionApplicationFile[],
  templateFiles: AppVersionApplicationFile[],
): AppVersionApplicationFile[] {
  return [...formApplicationFiles, ...templateFiles].filter(
    ({ uploadStatus }) =>
      uploadStatus &&
      [AppVersionFileUploadErrorStatus.None, AppVersionFileUploadErrorStatus.Failure].includes(uploadStatus),
  );
}

export function getAlreadyUploadedFiles(
  formApplicationFiles: AppVersionApplicationFile[],
  templateFiles: AppVersionApplicationFile[],
): Record<string, AppVersionApplicationFile> {
  const succeededFiles = [...formApplicationFiles, ...templateFiles]
    .filter(({ uploadStatus }) => uploadStatus && [AppVersionFileUploadErrorStatus.Success].includes(uploadStatus))
    .map((file) => ({ ...file }));

  return succeededFiles.reduce((accum, current) => {
    const internalFileId = toString(current?.id);

    return {
      ...accum,
      [internalFileId]: current,
    };
  }, {});
}

export function getUploadStatus(
  field: AppVersionApplicationFile,
  fulfilleds?: Record<string, AppVersionApplicationFile>,
  rejecteds?: Record<string, AppVersionApplicationFile>,
): AppVersionFileUploadErrorStatus | undefined {
  if (fulfilleds?.[toString(field?.id)]) {
    return AppVersionFileUploadErrorStatus.Success;
  } else if (rejecteds?.[toString(field?.id)]) {
    return AppVersionFileUploadErrorStatus.Failure;
  } else {
    return field?.uploadStatus;
  }
}

export function getUploadId(
  field: AppVersionApplicationFile,
  fulfilleds?: Record<string, AppVersionApplicationFile>,
): string | undefined {
  return fulfilleds?.[toString(field?.id)] ? fulfilleds[toString(field?.id)].uploadId : field?.uploadId;
}

export function transformUploadedFileResponseInfoToLocalApplicationFile(
  uploadedFile: {
    readonly checksum: string;
    readonly id: string;
    readonly targetLocation?: string;
    readonly artifactType: 'APPLICATION' | 'SW_PACKAGE';
    readonly name: string;
    readonly size: number;
    readonly order: number;
  },
  internalId?: string,
): AppVersionApplicationFile {
  return {
    checksum: uploadedFile.checksum,
    uploadId: uploadedFile.id,
    uploadStatus: AppVersionFileUploadErrorStatus.Success,
    targetLocation: uploadedFile.targetLocation,
    type:
      uploadedFile.artifactType === 'SW_PACKAGE'
        ? SystemPackageArtifactType.SwPackage
        : SystemPackageArtifactType.Template,
    name: toString(uploadedFile.name),
    size: uploadedFile.size,
    id: internalId,
    componentType: uploadedFile.artifactType as AppVersionApplicationFile['componentType'],
    order: uploadedFile.order,
  };
}

export function checkExistingTemplateIdsWereRequested(
  existingTemplatedIds: string[],
  requestedTemplateIds: string[],
): boolean {
  return isEqual(existingTemplatedIds, requestedTemplateIds);
}

export function parseSystemPackageVersionValuesForRequest(values: SystemPackageAppVersion): SystemPackageAppVersion {
  return {
    ...values,
    build: {
      ...values.build,
      applicationFiles: values.build?.applicationFiles?.map(({ template, targetLocation, ...rest }) => ({
        targetLocation,
        ...rest,
        template: template.map((file) => ({
          ...file,
          componentType: 'SYSTEM_PACKAGE',
          ...(isString(targetLocation) && targetLocation?.length >= 1 && { targetLocation }),
        })),
      })),
    },
    configure: {
      ...values.configure,
      parameterTemplates: values.configure?.parameterTemplates?.map((parameterTemplate) => ({
        ...parameterTemplate,
        template: parameterTemplate.template.map((template) => ({ ...template, componentType: 'SYSTEM_PACKAGE' })),
      })),
    },
  };
}

// FILE EXTENSIONS VALIDATIONS
export function getFileExtensionFromFilename(filename: string): string {
  return filename.includes('.') ? `.${filename.split('.').pop()}` : '';
}

export function isFileExtensionRequiredForTargetLocation(extension: string): boolean {
  return allowedAndRequiredTargetLocationExtensionsForBuild.includes(extension.toLowerCase());
}

export function verifyIsSystemPackageVersionDeploymentRulesError(error?: FieldError): boolean {
  return error?.type === SystemPackageVersionFormError.NoEmptyNamesInDeploymentRules;
}

export function verifyHasSystemPackageVersionBuildErrorsApartDeploymentRules(
  buildErrors: FieldErrors<SystemPackageAppVersion>['build'],
): boolean {
  return (
    buildErrors &&
    Object.values(buildErrors).some((current) => {
      return Array.isArray(current) || !verifyIsSystemPackageVersionDeploymentRulesError(current as FieldError);
    })
  );
}

export function verifyHasSystemPackageVersionBuildErrorsDeploymentRulesOnly(
  buildErrors: FieldErrors<SystemPackageAppVersion>['build'],
): boolean {
  return (
    buildErrors &&
    Object.values(buildErrors).some((current) => {
      return !Array.isArray(current) && verifyIsSystemPackageVersionDeploymentRulesError(current as FieldError);
    })
  );
}
